import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { CalendarCheck, Check } from 'lucide-react';

export default function CheckIn() {
    const [status, setStatus] = useState(null);
    const [history, setHistory] = useState([]);
    const [mood, setMood] = useState(3);
    const [reflection, setReflection] = useState('');
    const [saving, setSaving] = useState(false);

    const load = () => {
        api.get('/checkin/status').then(r => setStatus(r.data));
        api.get('/checkin').then(r => setHistory(r.data));
    };
    useEffect(() => { load(); }, []);

    const setFreq = async (frequency) => {
        await api.post('/checkin/schedule', { frequency });
        toast.success('Innsjekking oppdatert');
        load();
    };

    const submit = async () => {
        setSaving(true);
        try {
            await api.post('/checkin', { mood, reflection });
            setReflection('');
            toast.success('Takk for innsjekkingen');
            load();
        } finally { setSaving(false); }
    };

    return (
        <div className="max-w-3xl mx-auto px-6 py-10 pb-24" data-testid="checkin-page">
            <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-sage-soft flex items-center justify-center text-sage-dark">
                    <CalendarCheck className="w-5 h-5"/>
                </div>
                <div>
                    <h1 className="font-display text-3xl font-bold tracking-tight">Innsjekking</h1>
                    <p className="text-inkSoft text-sm">En liten pust i hverdagen.</p>
                </div>
            </div>

            <div className="mt-8 rounded-2xl border border-sand bg-white p-6">
                <p className="text-sm font-medium text-ink">Hvor ofte vil du sjekke inn?</p>
                <div className="mt-3 flex gap-2" data-testid="frequency-buttons">
                    {[
                        { k: 'off', l: 'Av' },
                        { k: 'daily', l: 'Daglig' },
                        { k: 'weekly', l: 'Ukentlig' },
                    ].map(x => (
                        <button key={x.k} onClick={() => setFreq(x.k)}
                            data-testid={`freq-${x.k}`}
                            className={`px-4 py-2 rounded-full text-sm transition-colors ${status?.frequency === x.k ? 'bg-sage text-bone' : 'bg-white border border-sand text-ink hover:border-sage/40'}`}>
                            {x.l}
                        </button>
                    ))}
                </div>
                {status?.due && (
                    <p className="mt-4 text-sm text-sage-dark flex items-center gap-1" data-testid="due-indicator">
                        <Check className="w-4 h-4"/> Klar for ny innsjekking nå
                    </p>
                )}
            </div>

            <div className="mt-8 rounded-2xl border border-sand bg-white p-6">
                <h2 className="font-display text-lg font-semibold">Sjekk inn nå</h2>
                <p className="text-sm text-inkSoft mt-1">Hvordan har dagen vært, sånn overordnet?</p>
                <div className="mt-4 flex gap-2" data-testid="checkin-mood-buttons">
                    {[1,2,3,4,5].map(n => (
                        <button key={n} onClick={() => setMood(n)}
                            data-testid={`checkin-mood-${n}`}
                            className={`w-10 h-10 rounded-full text-sm font-medium transition-all ${mood === n ? 'bg-sage text-bone ring-4 ring-offset-2 ring-offset-bone ring-sage/30' : 'bg-sand/50 text-ink'}`}>
                            {n}
                        </button>
                    ))}
                </div>
                <Textarea value={reflection} onChange={e => setReflection(e.target.value)} placeholder="En kort refleksjon? (valgfritt)" className="mt-4 bg-white border-sand rounded-xl" data-testid="reflection-input"/>
                <Button onClick={submit} disabled={saving} className="mt-4 rounded-full bg-sage hover:bg-sage-dark text-bone" data-testid="submit-checkin-btn">
                    {saving ? 'Lagrer…' : 'Lagre innsjekking'}
                </Button>
            </div>

            <h2 className="font-display text-xl font-semibold mt-12">Historikk</h2>
            <div className="mt-4 space-y-2">
                {history.length === 0 && <p className="text-sm text-inkSoft">Ingen innsjekkinger ennå.</p>}
                {history.map(h => (
                    <div key={h.checkin_id} className="p-4 rounded-xl border border-sand bg-white" data-testid={`checkin-${h.checkin_id}`}>
                        <p className="text-sm"><span className="font-medium">Humør {h.mood}/5</span> · <span className="text-inkSoft">{new Date(h.created_at).toLocaleString('nb-NO')}</span></p>
                        {h.reflection && <p className="text-sm text-inkSoft mt-1 whitespace-pre-wrap">{h.reflection}</p>}
                    </div>
                ))}
            </div>
        </div>
    );
}
